if (!customElements.get('compare-drawer')) {
  const COMPARE_STORAGE_KEY = 'theme-product-compare';
  const COMPARE_TOGGLE_KEY = 'theme-product-compare-toggle';
  const COMPARE_LAST_KEY = 'theme-product-compare-last';

  // Each drawer keeps its own list. The drawer showing on the page decides which list is in use.
  const compareStore = {
    drawer: '',
    max: 5,
    limitText: '',
    cache: {},

    configure({ drawer, max, limitText }) {
      this.drawer = drawer;
      this.max = max;
      this.limitText = limitText;
    },

    storageKey(drawer = this.drawer) {
      return `${COMPARE_STORAGE_KEY}-${drawer}`;
    },

    // Every checkbox reads the list, so it's parsed once and kept until it changes
    read(drawer = this.drawer) {
      if (!drawer) return [];

      if (!this.cache[drawer]) {
        let items = [];

        try {
          const parsed = JSON.parse(window.localStorage.getItem(this.storageKey(drawer)) || '[]');
          if (Array.isArray(parsed)) items = parsed;
        } catch (error) {
          // Storage may be unavailable (private browsing), so the list only lives in memory
        }

        this.cache[drawer] = items
          .filter(item => item && Number(item.id))
          .map(item => ({ ...item, id: Number(item.id) }));
      }

      return this.cache[drawer].slice(0, this.max);
    },

    forget(drawer = this.drawer) {
      delete this.cache[drawer];
    },

    write(items) {
      if (!this.drawer) return;

      this.cache[this.drawer] = items.slice(0, this.max);

      try {
        window.localStorage.setItem(this.storageKey(), JSON.stringify(this.cache[this.drawer]));
        if (items.length) window.localStorage.setItem(COMPARE_LAST_KEY, this.drawer);
      } catch (error) {
        // Storage may be unavailable (private browsing, quota exceeded)
      }

      this.emit();
    },

    lastUsed() {
      try {
        return window.localStorage.getItem(COMPARE_LAST_KEY);
      } catch (error) {
        return null;
      }
    },

    emit() {
      document.dispatchEvent(new CustomEvent('compare:change', {
        detail: { items: this.read() }
      }));
    },

    ids() {
      return this.read().map(item => item.id);
    },

    has(id) {
      return this.ids().includes(Number(id));
    },

    add(product) {
      const items = this.read();
      if (items.some(item => item.id === product.id)) return true;

      if (items.length >= this.max) return false;

      items.push(product);
      this.write(items);
      return true;
    },

    remove(id) {
      this.write(this.read().filter(item => item.id !== Number(id)));
    },

    clear() {
      this.write([]);
    },

    refresh(products) {
      const items = this.read();
      let changed = false;

      products.forEach(product => {
        const item = items.find(candidate => candidate.id === product.id);
        if (!item) return;

        ['title', 'url', 'image', 'price'].forEach(key => {
          if (product[key] !== undefined && item[key] !== product[key]) {
            item[key] = product[key];
            changed = true;
          }
        });
      });

      if (changed) this.write(items);
    },

    readVisible(fallback) {
      try {
        const stored = window.localStorage.getItem(COMPARE_TOGGLE_KEY);
        if (stored) return stored === 'on';
      } catch (error) {
        // Storage may be unavailable
      }

      return fallback;
    },

    // In the theme editor, a new default replaces the choice remembered while previewing the old one
    forgetVisibleIfDefaultChanged(drawer, fallback) {
      try {
        const key = `${COMPARE_TOGGLE_KEY}-default-${drawer}`;
        const value = fallback ? 'on' : 'off';
        if (window.localStorage.getItem(key) === value) return;

        window.localStorage.removeItem(COMPARE_TOGGLE_KEY);
        window.localStorage.setItem(key, value);
      } catch (error) {
        // Storage may be unavailable
      }
    },

    isVisible() {
      return document.documentElement.classList.contains('compare-checkboxes-on');
    },

    setVisible(visible, persist = true) {
      document.documentElement.classList.toggle('compare-checkboxes-on', visible);

      if (persist) {
        try {
          window.localStorage.setItem(COMPARE_TOGGLE_KEY, visible ? 'on' : 'off');
        } catch (error) {
          // Storage may be unavailable
        }
      }

      document.dispatchEvent(new CustomEvent('compare:toggle', {
        detail: { visible }
      }));
    }
  };

  window.compareStore = compareStore;

  // Adds a stylesheet or script the page doesn't have yet. They go in the drawer's section, where their
  // tags used to be, so the styles keep their place in the cascade.
  const loadAsset = (url, container) => {
    const isScript = url.split('?')[0].endsWith('.js');
    const existing = Array.from(document.querySelectorAll(isScript ? 'script[src]' : 'link[rel="stylesheet"]'))
      .find(element => element.getAttribute(isScript ? 'src' : 'href') === url);

    if (existing) {
      if (isScript || existing.sheet) return Promise.resolve();
      return new Promise(resolve => {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', resolve, { once: true });
      });
    }

    return new Promise(resolve => {
      const element = document.createElement(isScript ? 'script' : 'link');

      if (isScript) {
        element.src = url;
      } else {
        element.rel = 'stylesheet';
        element.href = url;
      }

      element.addEventListener('load', resolve, { once: true });
      element.addEventListener('error', resolve, { once: true });
      container.appendChild(element);
    });
  };

  const removeRootClasses = () => {
    const root = document.documentElement;

    root.classList.remove('compare-enabled', 'compare-checkboxes-forced', 'compare-drawer-offset', 'compare-toggle-off');
    Array.from(root.classList)
      .filter(name => name.startsWith('compare-position--'))
      .forEach(name => root.classList.remove(name));
  };

  class CompareCheckbox extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.sync = this.sync.bind(this);
      this.hideMessage = this.hideMessage.bind(this);
    }

    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;

      this.input.addEventListener('change', this.onChange);
      document.addEventListener('compare:change', this.sync);
      this.sync();
    }

    disconnectedCallback() {
      this.input?.removeEventListener('change', this.onChange);
      document.removeEventListener('compare:change', this.sync);
      this.hideMessage();
    }

    sync() {
      this.input.checked = compareStore.has(this.dataset.productId);
    }

    onChange() {
      if (!this.input.checked) {
        compareStore.remove(this.dataset.productId);
        return;
      }

      const added = compareStore.add({
        id: Number(this.dataset.productId),
        title: this.dataset.productTitle,
        url: this.dataset.productUrl,
        image: this.dataset.productImage,
        price: this.dataset.productPrice
      });

      if (!added) {
        this.input.checked = false;
        this.showMessage(compareStore.limitText);
      }
    }

    showMessage(text) {
      if (!text) return;

      if (!this.message) {
        this.message = document.createElement('span');
        this.message.className = 'card-compare__message';
        this.message.setAttribute('role', 'status');
        this.appendChild(this.message);
      }

      const card = this.closest('.card-wrapper') || this.parentElement;
      this.style.setProperty('--compare-card-width', `${card.offsetWidth}px`);
      this.message.textContent = text;
      this.classList.add('card-compare--message');

      clearTimeout(this.messageTimeout);
      this.messageTimeout = setTimeout(this.hideMessage, 4000);
      document.addEventListener('pointerdown', this.hideMessage);
    }

    hideMessage() {
      clearTimeout(this.messageTimeout);
      document.removeEventListener('pointerdown', this.hideMessage);
      this.classList.remove('card-compare--message');
    }
  }

  class CompareToggle extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.sync = this.sync.bind(this);
    }

    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;

      this.button.addEventListener('click', this.onClick);
      document.addEventListener('compare:toggle', this.sync);
      this.sync();

      document.dispatchEvent(new CustomEvent('compare:toggles-change'));
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
      document.removeEventListener('compare:toggle', this.sync);

      document.dispatchEvent(new CustomEvent('compare:toggles-change'));
    }

    sync() {
      this.button.setAttribute('aria-checked', compareStore.isVisible() ? 'true' : 'false');
    }

    onClick() {
      compareStore.setVisible(!compareStore.isVisible());
    }
  }

  class CompareDrawer extends HTMLElement {
    constructor() {
      super();

      this.tab = this.querySelector('.js-compare-tab');
      this.panel = this.querySelector('.compare-drawer__panel');
      this.list = this.querySelector('.js-compare-list');
      this.count = this.querySelector('.js-compare-count');
      this.empty = this.querySelector('.js-compare-empty');
      this.listWrapper = this.querySelector('.js-compare-body');
      [this.prevArrow, this.nextArrow] = this.querySelectorAll('.js-compare-arrow');
      this.openButton = this.querySelector('.js-compare-open');
      this.clearButton = this.querySelector('.js-compare-clear');
      this.closeButton = this.querySelector('.js-compare-close');

      this.max = Number(this.dataset.max) || 5;
      this.mobileQuery = window.matchMedia('(max-width: 749.98px)');
      this.requestedIds = '';
      this.lastCount = null;
      this.isOpen = false;
      this.isSelected = false;

      this.onTabClick = this.onTabClick.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onClear = this.onClear.bind(this);
      this.onOpenModal = this.onOpenModal.bind(this);
      this.onPrefetch = this.onPrefetch.bind(this);
      this.onListClick = this.onListClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onModalClick = this.onModalClick.bind(this);
      this.onLoaded = this.onLoaded.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onToggle = this.onToggle.bind(this);
      this.onStorage = this.onStorage.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.updateListFade = this.updateListFade.bind(this);
      this.onArrowClick = this.onArrowClick.bind(this);
      this.syncWithModal = this.syncWithModal.bind(this);
      this.applyLayout = this.applyLayout.bind(this);
      this.onTogglesChange = debounce(this.updateCheckboxFallback.bind(this), 100);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onSectionDeselect = this.onSectionDeselect.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
    }

    connectedCallback() {
      // Looked up here, not in the constructor, so a re-rendered section never picks up the old one's modal
      this.modal = document.getElementById(`CompareModal-${this.dataset.sectionId}`);
      this.productCompare = this.modal?.querySelector('product-compare');

      this.tab.addEventListener('click', this.onTabClick);
      this.closeButton?.addEventListener('click', this.onClose);
      this.clearButton.addEventListener('click', this.onClear);
      this.openButton.addEventListener('click', this.onOpenModal);
      this.openButton.addEventListener('pointerenter', this.onPrefetch);
      this.openButton.addEventListener('focus', this.onPrefetch);
      this.list.addEventListener('click', this.onListClick);
      this.listWrapper.addEventListener('scroll', this.updateListFade, { passive: true });
      this.prevArrow?.addEventListener('click', this.onArrowClick);
      this.nextArrow?.addEventListener('click', this.onArrowClick);
      this.addEventListener('keydown', this.onKeyDown);
      this.modal?.addEventListener('click', this.onModalClick);
      this.modal?.addEventListener('product-compare:loaded', this.onLoaded);

      if (this.modal) {
        this.modalObserver = new MutationObserver(this.syncWithModal);
        this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open', 'class'] });
      }

      if (Shopify.designMode) {
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      CompareDrawer.elect();
    }

    disconnectedCallback() {
      this.modalObserver?.disconnect();

      const isModalOpen = Boolean(this.modal?.hasAttribute('open'));

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.onSectionSelect);
        document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);

        window.compareDrawerDesignState = {
          ...window.compareDrawerDesignState,
          [this.dataset.sectionId]: {
            isSelected: this.isSelected,
            isOpen: this.isOpen,
            isModalOpen
          }
        };
      }

      if (this.inUse) this.deactivate();

      if (Shopify.designMode) {
        // The modal is moved to the body when opened, so it outlives the section
        this.modal?.remove();
        if (window.movedModals) window.movedModals = window.movedModals.filter(id => id !== this.modal?.id);
        if (isModalOpen) document.body.classList.remove('overflow-hidden');
      }

      CompareDrawer.elect();
    }

    // With a comparison per collection, the list is stored against the collection being browsed
    get basketKey() {
      const base = this.dataset.drawerKey;
      if (this.dataset.scopeCollections !== 'true') return base;
      if (this.dataset.collection) return `${base}--${this.dataset.collection}`;

      // Off a collection page there is nothing to scope to, so the last list the customer used stays with them
      const last = compareStore.lastUsed() || '';
      return last === base || last.startsWith(`${base}--`) ? last : base;
    }

    /* ONE DRAWER PER PAGE */

    // A drawer limited to the current collection beats one that isn't limited. Pages that no drawer covers
    // show the list the customer used last, so it follows them around the store.
    static pickWinner(drawers) {
      const forced = drawers.find(drawer => drawer.dataset.sectionId === window.compareDrawerForced);
      if (forced) return forced;

      const priority = drawer => Number(drawer.dataset.priority) || 0;
      const top = Math.max(0, ...drawers.map(priority));
      if (top > 0) return drawers.find(drawer => priority(drawer) === top);

      const last = compareStore.lastUsed();

      return drawers.find(drawer => drawer.basketKey === last && compareStore.read(last).length > 0)
        || drawers.find(drawer => drawer.dataset.unlimited === 'true')
        || drawers[0];
    }

    // Drawers upgrade one by one, so a winner that hasn't upgraded yet activates itself when it does
    static elect() {
      const drawers = Array.from(document.querySelectorAll('compare-drawer'));
      const winner = CompareDrawer.pickWinner(drawers);

      drawers.forEach(drawer => {
        if (drawer !== winner && drawer.inUse) drawer.deactivate();
      });

      if (winner?.activate && !winner.inUse) winner.activate();
    }

    activate() {
      this.inUse = true;
      this.lastCount = null;
      this.requestedIds = '';

      compareStore.configure({
        drawer: this.basketKey,
        max: this.max,
        limitText: this.dataset.limitText || ''
      });

      this.applyRootClasses();
      this.applyLayout();
      this.mobileQuery.addEventListener('change', this.applyLayout);

      document.addEventListener('compare:change', this.onChange);
      document.addEventListener('compare:toggle', this.onToggle);
      document.addEventListener('compare:toggles-change', this.onTogglesChange);
      document.addEventListener('click', this.onDocumentClick);
      window.addEventListener('storage', this.onStorage);
      window.addEventListener('resizeend', this.onTogglesChange);
      window.addEventListener('resizeend', this.updateListFade);

      this.removeAttribute('hidden');

      // Checkboxes connected before this drawer, so they pick up its list here
      compareStore.emit();
      this.updateCheckboxFallback();

      if (Shopify.designMode) this.restoreDesignState();
    }

    deactivate() {
      this.inUse = false;
      this.close();
      if (this.modal?.hasAttribute('open')) this.modal.hide(true);

      this.mobileQuery.removeEventListener('change', this.applyLayout);
      document.removeEventListener('compare:change', this.onChange);
      document.removeEventListener('compare:toggle', this.onToggle);
      document.removeEventListener('compare:toggles-change', this.onTogglesChange);
      document.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('storage', this.onStorage);
      window.removeEventListener('resizeend', this.onTogglesChange);
      window.removeEventListener('resizeend', this.updateListFade);

      this.setAttribute('hidden', '');

      const root = document.documentElement;

      if (root.dataset.compareDrawer === this.dataset.drawerKey) {
        removeRootClasses();
        delete root.dataset.compareDrawer;
      }
    }

    applyRootClasses() {
      const root = document.documentElement;

      removeRootClasses();
      root.dataset.compareDrawer = this.dataset.drawerKey;

      if (this.dataset.checkboxesAvailable === 'false') return;

      root.classList.add('compare-enabled', `compare-position--${this.dataset.checkboxPosition}`);

      // Without the switch, the checkboxes behave as though a customer had turned it on
      const showToggle = this.dataset.showToggle !== 'false';
      const defaultVisible = this.dataset.checkboxesVisible === 'true';

      if (Shopify.designMode) compareStore.forgetVisibleIfDefaultChanged(this.dataset.drawerKey, defaultVisible);

      root.classList.toggle('compare-toggle-off', !showToggle);
      compareStore.setVisible(showToggle ? compareStore.readVisible(defaultVisible) : true, false);
    }

    // Horizontal drawers don't fit on mobile, so it always shows at the right middle there
    applyLayout() {
      const position = this.mobileQuery.matches ? 'right_center' : this.dataset.position;
      if (position === this.layoutPosition) return;

      if (this.layoutPosition) {
        this.classList.remove(`compare-drawer--${this.layoutPosition.split('_')[0]}`, `compare-drawer--${this.layoutPosition.replace('_', '-')}`);
      } else {
        Array.from(this.classList)
          .filter(name => /^compare-drawer--(bottom|right)/.test(name))
          .forEach(name => this.classList.remove(name));
      }

      this.classList.add('compare-drawer--no-transition', `compare-drawer--${position.split('_')[0]}`, `compare-drawer--${position.replace('_', '-')}`);
      this.layoutPosition = position;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.classList.remove('compare-drawer--no-transition'));
      });

      if (this.lastCount !== null) this.render();
    }

    // Without a visible switch on the page, customers would have no way to show the checkboxes
    updateCheckboxFallback() {
      if (this.dataset.checkboxesAvailable === 'false') return;

      const hasToggle = Array.from(document.querySelectorAll('compare-toggle'))
        .some(toggle => toggle.getClientRects().length > 0);

      const root = document.documentElement;
      const forced = !hasToggle;
      const changed = root.classList.contains('compare-checkboxes-forced') !== forced;

      root.classList.toggle('compare-checkboxes-forced', forced);

      if (changed && this.lastCount !== null) this.render();
    }

    /* RENDER */

    // The switch hides the whole feature, so the drawer slides away with it and keeps its list for later
    get compareVisible() {
      const root = document.documentElement;
      return root.classList.contains('compare-checkboxes-on') || root.classList.contains('compare-checkboxes-forced');
    }

    render() {
      const items = compareStore.read();
      const count = items.length;
      const isActive = this.isSelected === true || (count > 0 && this.compareVisible);

      this.classList.toggle('compare-drawer--active', isActive);
      document.documentElement.classList.toggle(
        'compare-drawer-offset',
        isActive && ['bottom_left', 'bottom_right'].includes(this.layoutPosition)
      );

      if (!isActive && this.isOpen) this.close();

      this.count.textContent = `(${count})`;

      if (this.lastCount !== null && count > this.lastCount) {
        this.count.classList.remove('compare-drawer__count--bump');
        void this.count.offsetWidth;
        this.count.classList.add('compare-drawer__count--bump');
      }

      this.lastCount = count;

      this.list.textContent = '';
      items.forEach(item => this.list.appendChild(this.buildItem(item)));

      if (count > 0) {
        // The prompt to add more gives way to the limit message once the drawer is full
        const template = count < this.max ? '.js-compare-placeholder-template' : '.js-compare-limit-template';
        this.list.appendChild(this.cloneTemplate(template));
      }

      this.empty.hidden = count > 0;
      this.list.hidden = count === 0;
      this.updateListFade();

      this.openButton.disabled = count < 2;
    }

    updateListFade() {
      const wrapper = this.listWrapper;
      const scrolled = Math.abs(wrapper.scrollLeft);
      const remaining = wrapper.scrollWidth - wrapper.clientWidth - scrolled;

      wrapper.classList.toggle('compare-drawer__body--fade', remaining > 1);
      if (this.prevArrow) this.prevArrow.disabled = scrolled <= 1;
      if (this.nextArrow) this.nextArrow.disabled = remaining <= 1;
    }

    onArrowClick(event) {
      const wrapper = this.listWrapper;
      const item = this.list.firstElementChild;
      const step = item ? item.offsetWidth + (parseFloat(getComputedStyle(this.list).columnGap) || 0) : wrapper.clientWidth;
      const forward = event.currentTarget === this.nextArrow ? 1 : -1;
      const direction = getComputedStyle(wrapper).direction === 'rtl' ? -1 : 1;

      wrapper.scrollBy({ left: step * forward * direction, behavior: 'smooth' });
    }

    cloneTemplate(selector) {
      return this.querySelector(selector).content.firstElementChild.cloneNode(true);
    }

    buildItem(item) {
      const element = this.cloneTemplate('.js-compare-item-template');
      element.dataset.productId = item.id;

      const image = element.querySelector('img');
      if (item.image) {
        image.src = item.image;
      } else {
        image.remove();
      }

      const link = element.querySelector('a');
      link.textContent = item.title || '';
      if (this.isSafeUrl(item.url)) link.href = item.url;

      element.querySelector('.compare-drawer__item-price').textContent = item.price || '';

      const remove = element.querySelector('.js-compare-remove');
      remove.dataset.productId = item.id;
      remove.setAttribute('aria-label', (this.dataset.removeText || '').replace('[product]', item.title || ''));

      return element;
    }

    isSafeUrl(url) {
      try {
        return new URL(url, window.location.origin).origin === window.location.origin;
      } catch (error) {
        return false;
      }
    }

    /* DRAWER */

    onTabClick() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
        this.loadComparisonAssets();
      }
    }

    open() {
      this.isOpen = true;
      this.classList.add('compare-drawer--open');
      this.tab.setAttribute('aria-expanded', 'true');
      this.panel.inert = false;
    }

    close(returnFocus = false) {
      this.isOpen = false;
      this.classList.remove('compare-drawer--open');
      this.tab.setAttribute('aria-expanded', 'false');
      this.panel.inert = true;

      if (returnFocus) this.tab.focus();
    }

    onClose() {
      this.close(true);
    }

    onKeyDown(event) {
      if (event.key !== 'Escape' || !this.isOpen) return;
      this.close(true);
    }

    onDocumentClick(event) {
      // The list re-renders on remove, so the clicked button may already be detached
      if (!this.isOpen || this.isSelected || event.composedPath().includes(this)) return;
      if (event.target.closest('compare-checkbox, .popup-modal')) return;

      this.close();
    }

    onClear() {
      compareStore.clear();
    }

    onListClick(event) {
      const button = event.target.closest('.js-compare-remove');
      if (!button) return;

      const buttons = Array.from(this.list.querySelectorAll('.js-compare-remove'));
      const index = buttons.indexOf(button);

      compareStore.remove(button.dataset.productId);

      const nextButtons = this.list.querySelectorAll('.js-compare-remove');
      (nextButtons[index] || nextButtons[index - 1] || this.closeButton || this.tab).focus();
    }

    onChange() {
      this.render();

      if (!this.modal?.hasAttribute('open')) return;

      const ids = this.getComparisonIds();
      if (ids.length) {
        this.loadComparison(ids);
      } else {
        this.modal.hide();
      }
    }

    onToggle() {
      this.render();
    }

    onStorage(event) {
      if (event.key === compareStore.storageKey()) {
        compareStore.forget();
        compareStore.emit();
      }
      if (event.key === COMPARE_TOGGLE_KEY && this.dataset.showToggle !== 'false') {
        compareStore.setVisible(event.newValue === 'on', false);
      }
    }

    /* COMPARISON */

    getPreviewIds() {
      return (this.dataset.previewProducts || '').split(',').map(Number).filter(Boolean);
    }

    getComparisonIds() {
      const ids = compareStore.ids();
      if (ids.length || !Shopify.designMode) return ids;
      return this.getPreviewIds();
    }

    loadComparison(ids) {
      if (!this.productCompare) return;

      const key = ids.join(',');
      if (key === this.requestedIds) return;
      this.requestedIds = key;

      customElements.whenDefined('product-compare').then(() => {
        this.productCompare.setProducts(ids);
      });
    }

    // The comparison only matters once a customer heads for it, so its script and styles load then
    loadComparisonAssets() {
      const container = this.closest('.shopify-section') || document.body;
      const urls = (this.dataset.comparisonAssets || '').split(' ').filter(Boolean);

      return Promise.all(urls.map(url => loadAsset(url, container)));
    }

    onPrefetch() {
      this.loadComparisonAssets();

      const ids = this.getComparisonIds();
      if (ids.length > 1) this.loadComparison(ids);
    }

    onOpenModal() {
      this.openModal(this.tab);
    }

    async openModal(opener) {
      const ids = this.getComparisonIds();
      if (!this.modal || !ids.length) return;

      const isLoading = this.productCompare.dataset.compareIsLoading === 'true';
      if (!isLoading && !this.productCompare.querySelector('slider-component')) this.requestedIds = '';

      this.loadComparison(ids);
      await this.loadComparisonAssets();

      this.close();
      this.modal.show(opener);
    }

    // Slides the drawer away while the comparison is open, and back once it starts closing
    syncWithModal() {
      const isShowing = this.modal.hasAttribute('open') && !this.modal.classList.contains('popup-modal--closing');
      this.classList.toggle('compare-drawer--suspended', isShowing);
    }

    onModalClick(event) {
      const button = event.target.closest('.js-compare-remove');
      if (!button) return;

      button.closest('li')?.classList.add('compare-column--removing');
      compareStore.remove(button.dataset.productId);
    }

    onLoaded(event) {
      compareStore.refresh(event.detail.products);
    }

    /* THEME EDITOR */

    // The drawer being edited shows even where another drawer would normally win
    forceInEditor() {
      window.compareDrawerForced = this.dataset.sectionId;
      CompareDrawer.elect();
    }

    onSectionSelect(event) {
      if (event.detail.sectionId !== this.dataset.sectionId) return;

      this.forceInEditor();
      this.isSelected = true;
      this.render();
      this.open();
    }

    onSectionDeselect(event) {
      if (event.detail.sectionId !== this.dataset.sectionId) return;

      this.isSelected = false;
      this.close();
      if (this.modal?.hasAttribute('open')) this.modal.hide(true);
      if (this.inUse) this.render();

      if (window.compareDrawerForced === this.dataset.sectionId) {
        window.compareDrawerForced = null;
        CompareDrawer.elect();
      }
    }

    onBlockSelect(event) {
      if (event.detail.sectionId !== this.dataset.sectionId) return;

      this.forceInEditor();
      if (!this.modal?.hasAttribute('open')) this.openModal(this.tab);
    }

    restoreDesignState() {
      const state = window.compareDrawerDesignState?.[this.dataset.sectionId];
      if (!state) return;

      delete window.compareDrawerDesignState[this.dataset.sectionId];
      this.isSelected = state.isSelected === true;
      this.render();

      if (state.isOpen) this.open();
      if (state.isModalOpen) this.openModal(this.tab);
    }
  }

  customElements.define('compare-checkbox', CompareCheckbox);
  customElements.define('compare-toggle', CompareToggle);
  customElements.define('compare-drawer', CompareDrawer);
}
